/**
 * Guest Hub — public NFC landing page.  Route:  /hub/:slug
 *
 * See STANDING RULES in `components/loyalty/ThemeProvider.jsx`.
 * Mobile-first (390×844), full-width, filled viewport, outlet-themed.
 * The three destinations (Menu / Membership / Feedback) hide themselves
 * when the underlying data is missing.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Sparkles, MessageSquare, WalletCards, ArrowRight } from 'lucide-react';

import { GuestLoyaltyThemeProvider } from '../components/loyalty/ThemeProvider';

const API = process.env.REACT_APP_BACKEND_URL;


export default function GuestHub() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, error: '', data: null });

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/public/hub/${encodeURIComponent(slug || '')}`)
      .then((res) => { if (alive) setState({ loading: false, error: '', data: res.data }); })
      .catch((e) => {
        if (!alive) return;
        const msg = e?.response?.status === 404
          ? 'This link is no longer active.'
          : 'Something went wrong. Please try again.';
        setState({ loading: false, error: msg, data: null });
      });
    return () => { alive = false; };
  }, [slug]);

  if (state.loading) {
    return (
      <GuestLoyaltyThemeProvider mode="dark"
                                 className="min-h-screen w-full flex items-center justify-center px-6">
        <div className="text-sm opacity-70" data-testid="hub-loading">Loading…</div>
      </GuestLoyaltyThemeProvider>
    );
  }

  if (state.error || !state.data) {
    return (
      <GuestLoyaltyThemeProvider mode="dark"
                                 className="min-h-screen w-full flex items-center justify-center px-6">
        <div className="max-w-sm text-center" data-testid="hub-error">
          <div className="text-lg font-medium mb-1">Hub unavailable</div>
          <div className="text-sm opacity-70">{state.error || 'Please try again shortly.'}</div>
        </div>
      </GuestLoyaltyThemeProvider>
    );
  }

  const { outlet, theme, cards } = state.data;

  const visibleCards = [
    cards.menu.enabled && {
      key: 'menu',
      title: 'View the menu',
      subtitle: "Today's dishes and drinks",
      icon: BookOpen,
      to: cards.menu.url,
    },
    cards.membership.enabled && {
      key: 'membership',
      title: 'Explore membership',
      subtitle: 'Privileges, vouchers and perks',
      icon: Sparkles,
      to: cards.membership.url,
    },
    // ITER-hub-member-access — 3rd card: existing-member wallet.
    // Renders only when backend sets member_access.enabled true, which
    // itself mirrors the canonical loyalty publish rules. Older hub
    // responses without this field naturally hide the card.
    cards.member_access?.enabled && {
      key: 'member_access',
      title: 'Access your membership',
      subtitle: 'View your membership, vouchers and wallet',
      icon: WalletCards,
      to: cards.member_access.url,
    },
    cards.feedback.enabled && {
      key: 'feedback',
      title: 'Share feedback',
      subtitle: 'Tell us how the visit went',
      icon: MessageSquare,
      to: cards.feedback.url,
    },
  ].filter(Boolean);

  return (
    <GuestLoyaltyThemeProvider
      config={theme} mode="dark"
      className="min-h-screen w-full flex flex-col"
    >
      {/* Ambient watermark pattern to give the empty background a warm, brand-tinted glow. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            `radial-gradient(120% 60% at 50% 0%, var(--pattern-color), transparent 60%),` +
            `radial-gradient(90% 60% at 50% 110%, ${withAlpha('--primary', 0.28)}, transparent 55%)`,
        }}
      />

      <div
        className="relative flex-1 w-full mx-auto flex flex-col px-5 sm:px-6 pt-12 sm:pt-16 pb-14
                   md:max-w-xl"
        data-testid="guest-hub"
      >
        {/* Header block — logo, welcome, outlet name, with restrained shimmer. */}
        <header className="guest-shimmer rounded-3xl px-5 py-8 mb-10 text-center
                           border"
                style={{
                  borderColor: 'var(--border-subtle)',
                  background: `linear-gradient(180deg, ${withAlpha('--gold', 0.06)} 0%, transparent 100%)`,
                }}
                data-testid="hub-header">
          {outlet.logo_url ? (
            <img
              src={outlet.logo_url}
              alt={outlet.name}
              className="h-24 w-24 rounded-full object-cover mx-auto mb-5"
              style={{ boxShadow: `0 0 0 2px var(--gold), 0 12px 40px ${withAlpha('--primary', 0.55)}` }}
              data-testid="hub-outlet-logo"
            />
          ) : (
            <div className="mx-auto mb-5 h-24 w-24 rounded-full flex items-center justify-center
                            text-3xl font-serif"
                 style={{
                   background: 'var(--primary-deep)', color: 'var(--gold)',
                   boxShadow: `0 0 0 2px var(--gold)`,
                 }}
                 data-testid="hub-outlet-logo-fallback">
              {(outlet.name || '·').charAt(0)}
            </div>
          )}
          <div className="text-[11px] uppercase tracking-[0.32em] opacity-70 mb-3">
            Welcome to
          </div>
          <div className="text-3xl sm:text-4xl font-serif leading-tight"
               style={{ color: 'var(--ink)' }}
               data-testid="hub-outlet-name">
            {outlet.name}
          </div>
          {/* Gold rule under the name — the accent presence the spec asks for. */}
          <div
            className="mx-auto mt-4 h-[2px] rounded-full"
            style={{ background: 'var(--gold)', width: '48px' }}
          />
          {outlet.city ? (
            <div className="text-sm opacity-70 mt-3" data-testid="hub-outlet-city">
              {outlet.city}
            </div>
          ) : null}
        </header>

        {/* Card stack */}
        {visibleCards.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-center opacity-70
                          text-base"
               data-testid="hub-no-cards">
            Nothing here yet — check back soon.
          </div>
        ) : (
          <div className="space-y-4" data-testid="hub-cards">
            {visibleCards.map((c) => (
              <HubCard key={c.key} card={c} />
            ))}
          </div>
        )}

        {/* Spacer so the footnote sits at the bottom on tall phones. */}
        <div className="flex-1" />

        <div className="mt-12 text-center text-[11px] uppercase tracking-[0.28em] opacity-45"
             data-testid="hub-footer">
          Tap to enter · powered by RestroPulse
        </div>
      </div>
    </GuestLoyaltyThemeProvider>
  );
}


function HubCard({ card }) {
  const Icon = card.icon;
  return (
    <Link
      to={card.to}
      data-testid={`hub-card-${card.key}`}
      className="hub-card group relative flex items-center gap-4 rounded-2xl px-5 py-5
                 transition-all duration-200 min-h-[76px]
                 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.985]"
      style={{
        background:  `linear-gradient(180deg, ${withAlpha('--surface', 1)} 0%, ${withAlpha('--primary-deep', 0.6)} 100%)`,
        border:      `1px solid ${withAlpha('--gold', 0.28)}`,
        boxShadow:   `0 1px 0 ${withAlpha('--gold', 0.06)} inset, 0 10px 30px ${withAlpha('--primary', 0.35)}`,
      }}
    >
      <div
        className="shrink-0 flex h-12 w-12 items-center justify-center rounded-xl"
        style={{
          background: `linear-gradient(135deg, ${withAlpha('--gold', 0.18)} 0%, ${withAlpha('--gold', 0.06)} 100%)`,
          border: `1px solid ${withAlpha('--gold', 0.35)}`,
          color: 'var(--gold)',
        }}
      >
        <Icon className="w-5 h-5" strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[17px] font-medium leading-tight"
             style={{ color: 'var(--ink)' }}>
          {card.title}
        </div>
        <div className="text-sm opacity-70 mt-1 leading-snug">{card.subtitle}</div>
      </div>
      <ArrowRight
        className="w-5 h-5 shrink-0 transition-transform group-hover:translate-x-1"
        style={{ color: 'var(--gold)' }}
      />
    </Link>
  );
}


/**
 * Compose `rgba(var(--x-r), var(--x-g), var(--x-b), a)` at authoring
 * time isn't possible with plain hex CSS variables, so we produce a
 * `color-mix()` expression instead.  color-mix has broad support
 * (Safari 16.4+, Chrome 111+) which comfortably covers a modern
 * phone visiting an NFC tag.  Falls back to the raw var when the
 * browser does not support color-mix.
 */
function withAlpha(varName, alpha) {
  const clean = varName.replace(/^--/, '');
  const pct = Math.round(alpha * 100);
  return `color-mix(in srgb, var(--${clean}) ${pct}%, transparent)`;
}
